import { AssetClassCode } from '@api/api/entity-constants';
import { useDisableScroll } from '@helpers/hooks/use-disable-scroll/useDisableScroll';
import { ConditionSection } from '@terminal/components/condition-section/ConditionSection';
import { EXPANDED_VALUE } from '@terminal/components/expandable-drawer/ExpandableDrawer';
import { InsightDrawerAssetHeaderContent } from '@terminal/components/insight-drawer-header/InsightDrawerAssetHeaderContent';
import { InsightsLivePrice } from '@terminal/components/insights-live-price/InsightsLivePrice';
import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import { queryKeys } from '@terminal/routes/query-params';
import { getInsightCondition } from '@terminal/services/insights/utils/insight-utils';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { InsightWidgetData } from '@terminal/views/alfred/quick-analysis/use-chat/assistant-response.type';
import { AssessmentWidget } from '@terminal/widgets/assessment-widget/AssessmentWidget';
import { EntityHeaderWatchlist } from '@terminal/widgets/earnings-insight-drawer/earnings-insight-widget/components/entity-header-watchlist/EntityHeaderWatchlist';
import { InsightSummary } from '@terminal/widgets/insight-widget/components/insight-summary/InsightSummary';
import { InsightPastEpisodes } from '@terminal/widgets/past-episodes-widget/components/insight-past-episodes/InsightPastEpisodes';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { InsightsReaction } from '../insights-reaction/InsightsReaction';
import { ScenarioTriggerSection } from '../scenario-trigger-section/ScenarioTriggerSection';
import { InsightChartSection } from './InsightChartSection';
import styles from './InsightsDrawerContent.module.scss';
import { ScenarioInsightPill } from './ScenarioInsightPill';

export type InsightsDrawerContentProps = {
  insightsData: InsightWidgetData;
  header?: () => React.JSX.Element;
  showDrawerHeader?: boolean;
};

export const InsightsDrawerContent = ({
  insightsData,
  header,
  showDrawerHeader = false,
}: InsightsDrawerContentProps) => {
  useDisableScroll();
  const { t } = useTranslation(['scenarioCommander', 'widget']);
  const { params } = useQueryParams();
  const isExpanded = params[queryKeys.drawerExpanded] === EXPANDED_VALUE;

  const { insight, entity, snakeTs } = insightsData;

  const entityName = entity.name;
  const isFixedIncome =
    entity.asset_class === AssetClassCode.Fi && entity.class === 6;

  const episodeCount = insight.episodes.length;
  const condition = getInsightCondition(insight, t);

  return (
    <div className={styles.container} data-insights-drawer-container>
      <div className={styles.insightTopWrapper}>
        {!isExpanded &&
          (showDrawerHeader ? (
            <InsightDrawerAssetHeaderContent
              entity={entity}
              source={POSTHOG_TRACKING_SOURCES.SCENARIO_INSIGHT_DRAWER}
            />
          ) : (
            <div className={styles.entityAndPriceWrapper}>
              <EntityHeaderWatchlist entity={entity} logoSize={48} />
              <InsightsLivePrice
                entity={entity}
                showPricePercentage
                waitForLiveTick
              />
            </div>
          ))}
        {!showDrawerHeader && (
          <ScenarioInsightPill insight={insight} entity={entity} />
        )}
        {header && header()}
        <InsightSummary insight={insight} isFixedIncome={isFixedIncome} />
        <InsightsReaction
          entityName={entityName}
          episodeCount={episodeCount}
          isFixedIncome={isFixedIncome}
        />
        <InsightChartSection
          insightId={insight.id}
          bestHorizon={insight.metadata.horizon}
          isFixedIncome={isFixedIncome}
          snakeTs={snakeTs}
        />
      </div>
      <ScenarioTriggerSection
        entityName={entityName}
        entityTag={entity.tag}
        conditions={insight.conditions}
      />
      <div className={styles.deepDiveWrapper}>
        <div className={styles.styledTitle}>
          {t('scenarioCommander:deepDive')}
        </div>
        <ConditionSection condition={condition} />
        <InsightPastEpisodes
          insight={insight}
          entity={entity}
          variant={'insight-past-episodes'}
        />
      </div>
      <div>
        <AssessmentWidget
          isFixedIncome={isFixedIncome}
          gaugeValue={insight.metadata.stars}
          horizon={insight.metadata.horizon}
          median={insight.metadata.median_return}
          checklist={insight.metadata.high_conviction_report}
          hasHeader={false}
          titleElement={
            <div className={styles.styledTitle}>
              {t('scenarioCommander:scenarioQuality')}
            </div>
          }
        />
      </div>
    </div>
  );
};
